import * as Sentry from "@sentry/browser";
import { isShareRoute } from "@/utils/isShareRoute";

// Profile type definition matching what's stored in localStorage
export interface Profile {
  email: string;
  oktaUserId: string;
}

const PROFILE_LOCAL_STORAGE_KEY = "profile";

// Get environment, package name and version from environment variables defined in vite.config.js
// These are injected at build time from the .env files or CI/CD environment
const isDevelopment = import.meta.env.DEV;
const packageName = import.meta.env.VITE_PACKAGE_NAME;
const packageVersion = import.meta.env.VITE_PACKAGE_VERSION;
const environment = import.meta.env.VITE_ENVIRONMENT;

if (!isDevelopment && !isShareRoute()) {
  Sentry.init({
    dsn: "https://59d2ca7fbb5f765444be6d148b09bd08@o38470.ingest.us.sentry.io/4509244146384896",
    sendDefaultPii: true,
    release: `${packageName}@${packageVersion}`,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: ["localhost", /^https:\/\/hginsights\.com\/.*/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend: (event) => {
      window.dispatchEvent(
        new CustomEvent("sentry-error", {
          detail: {
            message:
              "An unexpected error occurred. Our team has been notified.",
            errorMessage: event.exception?.values?.[0]?.value,
            eventId: event.event_id,
          },
        })
      );
      return event;
    },
  });
} else {
  // In development, log to console instead of sending to Sentry
  console.info("Sentry disabled in development mode");
}

export const setSentryUser = (profile: Profile | null) => {
  if (isDevelopment) return;

  if (profile) {
    Sentry.setUser({
      id: profile.oktaUserId,
      email: profile.email,
    });
  } else {
    Sentry.setUser(null);
  }
};

if (!isDevelopment) {
  try {
    const profileData = localStorage.getItem(PROFILE_LOCAL_STORAGE_KEY);
    if (profileData) {
      const profile = JSON.parse(profileData) as Profile;
      setSentryUser(profile);
    }
  } catch (e) {
    console.error("Failed to set initial Sentry user", e);
  }
}
